<template>
    <div class="container" v-if="!isDataRequired">
        <v-title :lineage="[{'Checkout Pages':'checkout-pages'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Checkout Page
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'checkout-pages'}"></v-cancel>
                <r-button type="primary" @click="save(page)" :loading="isLoading">Save Checkout Page</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="newRecord"
            type="positive"
            title="Checkout page successfully created"
            class="stack-l">
        <span slot="content">
        Would you like to add <r-button type="link" @click="createNew">another checkout page</r-button> or go back to the <router-link :to="{name: 'checkout-pages'}">checkout pages list</router-link>?
            <template v-if="checkoutPageUrl">
                Or <a :href="checkoutPageUrl" target="_blank">preview</a> the checkout page you created.
            </template>
        </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.headerBgColor">Invalid header background color.</li>
            <li class="list-item" v-if="!$v.footerBgColor">Invalid footer background color.</li>
            <li class="list-item" v-if="!$v.footerColor">Invalid footer text color.</li>
            <li class="list-item" v-if="!$v.termsBgColor">Invalid terms background color.</li>
            <li class="list-item" v-if="!$v.termsColor">Invalid terms text color.</li>
            <li class="list-item" v-if="!$v.page.name.required">Please provide a name for the checkout page.</li>
            <li class="list-item" v-if="!$v.page.urlPathSegment.required">Please provide a custom URL path segment for
                the checkout page.
            </li>
            <li class="list-item" v-if="!$v.page.urlPathSegment.minLength">The custom URL path segment must be at least
                five (5) characters long.
            </li>
            <li class="list-item" v-if="!$v.page.planId.required">Please select a plan for the checkout page.</li>
            <li class="list-item" v-if="!$v.page.websiteId.required">Please select a website for the checkout page.</li>
            <li class="list-item" v-if="!$v.page.redirect.url.url">Please provide a valid redirection URL.</li>
            <li class="list-item" v-if="$v.page.redirect.timeout.$error">The redirect timeout must be numeric and equal
                to or greater than zero (0).
            </li>
            <li class="list-item" v-if="$v.page.quantityMin.$error">The minimum quantity form option must be numeric and
                equal to or greater than one (1).
            </li>
            <li class="list-item" v-if="$v.page.taxes.items.$error">All tax lines must have a name and rate must be positive
                decimal greater than zero (0).
            </li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Checkout Page Information</h2>
            <p slot="description" class="text-muted">Setup your checkout page for a specific website and plan and select
                the URL at which it can be accessed.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                data-cy="Name input"
                                auto-focus
                                label="Name"
                                :validate="$v.page.name"
                                v-model="page.name"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-checkbox
                                label="Enabled"
                                caption="Toggle on to enable access to this checkout page to your customers."
                                :value="true"
                                v-model="page.status"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Website"
                                help-text="Select a website to attach to this checkout page."
                                preselect-first
                                :options="websites"
                                v-model="page.websiteId"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Plan"
                                help-text="Select which plan to attach to this checkout page."
                                preselect-first
                                :options="plans"
                                v-model="page.planId"
                            />
                        </div>
                        <div class="grid-item grid-item-full">
                            <r-input
                                label="Custom URL path segment"
                                left-label="https://checkout.rebilly.com/checkout/"
                                help-text="e.g. https://checkout.rebilly.com/checkout/<your-custom-url>"
                                :validate="$v.page.urlPathSegment"
                                v-model="page.urlPathSegment"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Theme"
                                :help-text="themeCaption"
                                :options="themes"
                                v-model="page.theme"
                            />
                        </div>
                    </div>
                </div>
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item">
                            <r-checkbox
                                label="Custom Customer ID"
                                caption="Enable to use your own custom customer ID. The ID must be provided in the query string as 'customerId'. e.g.  https://checkout.rebilly.com/checkout/acme?customerId=myCustomId"
                                :value="true"
                                v-model="page.isCustomCustomerIdAllowed"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Page Header</h2>
            <p slot="description" class="text-muted">Provide a custom logo and background color for the checkout page
                header.</p>
            <div slot="content" class="tile">
                <div class="tile-content cursor-pointer" @click="toggleSection(sections.header)">
                    <div class="grid-content grid-unstackable">
                        <div class="grid-item"><h3>Page Header</h3></div>
                        <div class="grid-item no-flex text-right">
                            <v-toggle
                                @input="toggleSection(sections.header)"
                                :selected="page.hasMarkup(sections.header)"
                                :value="true"/>
                        </div>
                    </div>
                </div>
                <template v-if="page.hasMarkup(sections.header)">
                    <div class="tile-content tile-content-secondary">
                        <div class="grid-content">
                            <div class="grid-item grid-item-full">
                                <v-upload-logo
                                    :preview-url="logoPreviewUrl"
                                    @no-logo="setNoLogo"
                                    @update="updateLogo"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content tile-content-secondary">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <color-input
                                    label="Header Background Color"
                                    :validate="$v.page.markup.$each.$iter[page.getMarkupIndex(sections.header)].style.background"
                                    v-model="headerBgColor"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content tile-content-secondary" v-if="page.canAlignLogo">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <v-select
                                    :options="alignmentOptions"
                                    label="Logo Alignment"
                                    help-text="Change this value to customize the position of your logo within the page."
                                    v-model="page.logoAlignment"/>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </v-form-tile>
        <template v-if="isAdvancedTheme">
            <div class="divider"></div>
            <body-sections
                :page="page"
                @add-section="addBodySection"
                @input="updateBody"
                :validate="$v.page.markup"
                @toggle="toggleBodySections"/>
            <div class="divider"></div>
            <form-settings
                :page="page"
                :validate="$v.page"
                @input="updateFormSettings"/>
            <template v-if="canEnableExtras">
                <div class="divider"></div>
                <plan-offer
                    title="Suggested Upgrade"
                    :plans="plans"
                    :field="page.suggestedUpgrade"
                    @toggle="toggleSuggestedUpgrade"
                    @input="updateSuggestedUpgrade"
                    toggle-field="hasProration"
                    toggle-title="Has Proration"
                    toggle-caption="Enable to give pro rata credits if the customer changes his plan in the future.">
                    <p class="text-muted">Suggest a plan to the customer that is an upgrade from the normal offer.</p>
                </plan-offer>
                <div class="divider"></div>
                <plan-offer
                    title="One Time Offer"
                    :plans="plans"
                    :field="page.oneTimeOffer"
                    @toggle="toggleOTO"
                    @input="updateOTO"
                    toggle-field="isTiedToQuantity"
                    toggle-title="Tie To Quantity"
                    toggle-caption="Enable if you want to tie the one-time-offer to the quantity on your checkout page.">
                    <p class="text-muted">A way to increase sales by offering another related product or service to
                        customers that have just made a purchase.</p>
                </plan-offer>
            </template>
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Order Summary</h2>
                <p slot="description" class="text-muted">Description that appears right next to the order form. It
                    should include pricing information.</p>
                <div slot="content" class="tile">
                    <div class="tile-content cursor-pointer" @click="toggleSection(sections.orderSummary)">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item"><h3>Order Summary</h3></div>
                            <div class="grid-item no-flex text-right">
                                <v-toggle
                                    @input="toggleSection(sections.orderSummary)"
                                    :selected="page.hasMarkup(sections.orderSummary)"
                                    :value="true"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content" v-if="page.hasMarkup(sections.orderSummary)">
                        <div class="grid-content">
                            <div class="grid-item grid-item-full">
                                <v-editor
                                    class="stack-s"
                                    v-model="orderSummaryContent"/>
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Footer</h2>
                <p slot="description" class="text-muted">This statement will appear in the footer of your checkout
                    page.</p>
                <div slot="content" class="tile">
                    <div class="tile-content cursor-pointer" @click="toggleSection(sections.footer)">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item"><h3>Footer</h3></div>
                            <div class="grid-item no-flex text-right">
                                <v-toggle
                                    @input="toggleSection(sections.footer)"
                                    :selected="page.hasMarkup(sections.footer)"
                                    :value="true"/>
                            </div>
                        </div>
                    </div>
                    <template v-if="page.hasMarkup(sections.footer)">
                        <div class="tile-content">
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <v-editor
                                        class="stack-s"
                                        v-model="footerContent"/>
                                </div>
                                <div class="grid-item grid-item-half grid-item-break-half">
                                    <color-input
                                        label="Footer Background Color"
                                        :validate="$v.page.markup.$each.$iter[page.getMarkupIndex(sections.footer)].style.background"
                                        v-model="footerBgColor"/>
                                </div>
                                <div class="grid-item grid-item-half grid-item-break-half">
                                    <color-input
                                        label="Footer Font Color"
                                        :validate="$v.page.markup.$each.$iter[page.getMarkupIndex(sections.footer)].style.color"
                                        v-model="footerColor"/>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </v-form-tile>
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Terms</h2>
                <p slot="description" class="text-muted">This statement will appear in the terms section of your
                    checkout page.</p>
                <div slot="content" class="tile">
                    <div class="tile-content cursor-pointer" @click="toggleSection(sections.terms)">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item"><h3>Terms</h3></div>
                            <div class="grid-item no-flex text-right">
                                <v-toggle
                                    @input="toggleSection(sections.terms)"
                                    :selected="page.hasMarkup(sections.terms)"
                                    :value="true"/>
                            </div>
                        </div>
                    </div>
                    <template v-if="page.hasMarkup(sections.terms)">
                        <div class="tile-content">
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <v-editor
                                        class="stack-s"
                                        v-model="termsContent"/>
                                </div>
                                <div class="grid-item grid-item-half grid-item-break-half">
                                    <color-input
                                        label="Terms Background Color"
                                        :validate="$v.page.markup.$each.$iter[page.getMarkupIndex(sections.terms)].style.background"
                                        v-model="termsBgColor"/>
                                </div>
                                <div class="grid-item grid-item-half grid-item-break-half">
                                    <color-input
                                        label="Terms Font Color"
                                        :validate="$v.page.markup.$each.$iter[page.getMarkupIndex(sections.terms)].style.color"
                                        v-model="termsColor"/>
                                </div>
                            </div>
                        </div>
                        <div class="tile-content">
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <r-checkbox
                                        label="Pre-Check Terms"
                                        caption="Enable if you want to have the terms checkbox already toggled on in the checkout page."
                                        :value="true"
                                        v-model="page.isPreCheckedTerms"/>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </v-form-tile>
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Thank You Page</h2>
                <p slot="description" class="text-muted">After a successful checkout, the customer will see our thank
                    you page.</p>
                <div slot="content" class="tile">
                    <div class="tile-content cursor-pointer" @click="toggleSection(sections.thankYou)">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item"><h3>Thank You Page</h3></div>
                            <div class="grid-item no-flex text-right">
                                <v-toggle
                                    @input="toggleSection(sections.thankYou)"
                                    :selected="page.hasMarkup(sections.thankYou)"
                                    :value="true"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content tile-content-secondary" v-if="page.hasMarkup(sections.thankYou)">
                        <div class="grid-content">
                            <div class="grid-item grid-item-full">
                                <v-editor
                                    class="stack-s"
                                    v-model="thankYouContent"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content cursor-pointer" @click="toggleThankYouOTO">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item"><h3>Thank You Page One Time Offer</h3></div>
                            <div class="grid-item no-flex text-right">
                                <v-toggle
                                    @input="toggleThankYouOTO"
                                    :selected="page.hasThankYouOTO"
                                    :value="true"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content tile-content-secondary" v-if="page.hasThankYouOTO">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half" v-if="plans">
                                <v-select
                                    :options="plans"
                                    preselect-first
                                    label="Plan"
                                    v-model="page.thankYouOneTimeOffer.planId"
                                    help-text="Select the plan to offer to the customer."/>
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-checkbox
                                    label="Tie To Quantity"
                                    caption="Enable if you want to tie the one-time-offer to the quantity on your checkout page."
                                    :value="true"
                                    v-model="page.thankYouOneTimeOffer.isTiedToQuantity"/>
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
        </template>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Redirect Information</h2>
            <div slot="description">
                <p class="text-muted stack-m">After a successful checkout, the customer will see our invoice. However,
                    you have the option to designate a redirect URL for us to direct the user to, after waiting a number
                    of seconds.</p>
                <p class="text-muted">If you use '0' for the Redirect Timeout, the customer will be redirected instantly
                    (defaults to 5 seconds).</p>
            </div>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item">
                            <r-input
                                label="Redirect URL"
                                help-text="e.g. https://www.mywebsite.com/thanks"
                                :validate="$v.page.redirect.url"
                                v-model="page.redirect.url"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Redirect Timeout (seconds)"
                                help-text="The amount of time in seconds to wait before redirecting the user to your URL. e.g. 5"
                                :validate="$v.page.redirect.timeout"
                                v-model="page.redirect.timeout"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <template v-if="isFormUpdating">
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Danger Zone</h2>
                <div slot="content" class="tile tile-secondary">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item">
                                <p class="stack-m">Deleting is permanent and cannot be undone.</p>
                                <r-button type="danger" :loading="isLoading" class="inline-s" @click="deletePage">Delete Checkout Page</r-button>
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
        </template>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'checkout-pages'}"></v-cancel>
                <r-button type="primary" @click="save(page)" :loading="isLoading">Save Checkout Page</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import config from '@/common/config';
    import vTitle from '@/components/v-title';
    import {
        vCancel,
        vEditor,
        vErrorMessages,
        vFormTile,
        vSelect,
        vToggle,
        vUploadLogo,
    } from '@/components/form';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import CheckoutPageModel, {
        CheckoutPageMarkupModel,
        OneTimeOfferModel,
        SuggestedUpgradeModel,
        ThankYouOneTimeOfferModel,
    } from '@/common/models/checkout-page-model';
    import _ from '@/common/helpers';
    import {
        minLength, minValue, numeric, decimal, required, url,
    } from 'vuelidate/lib/validators';
    import sessionStore from '@/common/storage/session-store';
    import greaterThan from '@/common/validation/validators/greater-than';
    import isHexColor from '@/common/validation/validators/is-hex-color';
    import ColorInput from './components/color-input';
    import BodySections from './components/body-sections';
    import FormSettings from './components/form-settings';
    import PlanOffer from './components/plan-offer';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import cloningStorageKey from './cloning-storage-key';

    export default {
        name: 'checkout-page-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vInlineNotifications,
            vFormTile,
            vCancel,
            vSelect,
            vToggle,
            vUploadLogo,
            vEditor,
            ColorInput,
            BodySections,
            FormSettings,
            PlanOffer,
            vErrorMessages,
        },
        computed: {
            isDataRequired() {
                return !this.isDataReady;
            },
            themeCaption() {
                switch (this.page.theme) {
                    case CheckoutPageModel.Themes.classic:
                        return 'The classic theme is the most basic form of the checkout page with customization limited to the logo image.';
                    default:
                        // TODO consider adding descriptions for each theme
                        return `The ${_.unKebabCase(this.page.theme).toLowerCase()} theme enables advanced customization of the look and feel of the page.`;
                }
            },
            isAdvancedTheme() {
                return this.page.isAdvancedTheme;
            },
            canHidePlanSummary() {
                return this.page.canHidePlanSummary;
            },
            canEnableExtras() {
                // this includes suggested upgrade, one-time offer, thank-you offer
                return this.page.canEnableExtras;
            },
            checkoutPageUrl() {
                return `${config.VUE_APP_CHECKOUT_URL}/checkout/${this.page.urlPathSegment}`;
            },
            headerBgColor: {
                get: function get() {
                    return this.page.getMarkup({section: this.sections.header}).style.background;
                },
                set: function set(color) {
                    this.page.getMarkup({section: this.sections.header}).style.background = color;
                },
            },
            orderSummaryContent: {
                get: function get() {
                    return this.page.getMarkup({section: this.sections.orderSummary}).content;
                },
                set: function set(content) {
                    this.page.getMarkup({section: this.sections.orderSummary}).content = content;
                },
            },
            footerContent: {
                get: function get() {
                    return this.page.getMarkup({section: this.sections.footer}).content;
                },
                set: function set(content) {
                    this.page.getMarkup({section: this.sections.footer}).content = content;
                },
            },
            termsContent: {
                get: function get() {
                    return this.page.getMarkup({section: this.sections.terms}).content;
                },
                set: function set(content) {
                    this.page.getMarkup({section: this.sections.terms}).content = content;
                },
            },
            thankYouContent: {
                get: function get() {
                    return this.page.getMarkup({section: this.sections.thankYou}).content;
                },
                set: function set(content) {
                    this.page.getMarkup({section: this.sections.thankYou}).content = content;
                },
            },
            footerBgColor: {
                get: function get() {
                    const markup = this.page.getMarkup({section: this.sections.footer});
                    return markup && markup.style && markup.style.background;
                },
                set: function set(color) {
                    this.page.getMarkup({section: this.sections.footer}).style.background = color;
                },
            },
            footerColor: {
                get: function get() {
                    const markup = this.page.getMarkup({section: this.sections.footer});
                    return markup && markup.style && markup.style.color;
                },
                set: function set(color) {
                    this.page.getMarkup({section: this.sections.footer}).style.color = color;
                },
            },
            termsBgColor: {
                get: function get() {
                    const markup = this.page.getMarkup({section: this.sections.terms});
                    return markup && markup.style && markup.style.background;
                },
                set: function set(color) {
                    this.page.getMarkup({section: this.sections.terms}).style.background = color;
                },
            },
            termsColor: {
                get: function get() {
                    const markup = this.page.getMarkup({section: this.sections.terms});
                    return markup && markup.style && markup.style.color;
                },
                set: function set(color) {
                    this.page.getMarkup({section: this.sections.terms}).style.color = color;
                },
            },
            /**
             * Generate the URL of the image in the header content when present.
             * @returns {string|Object}
             */
            logoPreviewUrl() {
                const markup = this.page.getMarkup({section: this.sections.header});
                if (markup && markup.content.indexOf('<img') === 0) {
                    const regexp = /^<img src="(.+?)"\salt=".+?" \/>/;
                    return markup.content.replace(regexp, '$1');
                }
                return null;
            },
            alignmentOptions() {
                return Object.keys(CheckoutPageModel.LogoAlignments)
                    .map(position => ({
                        label: _.capitalize(position),
                        value: CheckoutPageModel.LogoAlignments[position],
                    }));
            },
        },
        data() {
            return {
                page: new CheckoutPageModel(),
                plans: [],
                websites: [],
                themes: Object
                    .values(CheckoutPageModel.Themes)
                    .map(value => ({label: _.unKebabCase(value), value})),
                /**
                 * @type {Object}
                 */
                sections: {...CheckoutPageMarkupModel.Sections},
                logo: null,
                fileId: null,
            };
        },
        validations: {
            page: {
                name: {required},
                urlPathSegment: {required, minLength: minLength(5)},
                quantityMin: {numeric, minValue: minValue(1)},
                planId: {required},
                websiteId: {required},
                taxes: {
                    items: {
                        $each: {
                            label: {required},
                            value: {decimal, greaterThan: greaterThan(0), required},
                        },
                    },
                },
                redirect: {
                    url: {url},
                    timeout: {numeric, minValue: minValue(0)},
                },
                markup: {
                    $each: {
                        style: {
                            background: {isHexColor},
                            color: {isHexColor},
                        },
                    },
                },
            },
            headerBgColor: (component) => {
                if (typeof component.headerBgColor === 'string') {
                    return isHexColor(component.headerBgColor);
                }
                return true;
            },
            footerBgColor: (component) => {
                if (typeof component.footerBgColor === 'string') {
                    return isHexColor(component.footerBgColor);
                }
                return true;
            },
            footerColor: (component) => {
                if (typeof component.footerColor === 'string') {
                    return isHexColor(component.footerColor);
                }
                return true;
            },
            termsBgColor: (component) => {
                if (typeof component.termsBgColor === 'string') {
                    return isHexColor(component.termsBgColor);
                }
                return true;
            },
            termsColor: (component) => {
                if (typeof component.termsColor === 'string') {
                    return isHexColor(component.termsColor);
                }
                return true;
            },
        },
        methods: {
            createNew() {
                this.$v.$reset();
                this.page = new CheckoutPageModel();
                this.$router.push({name: 'add-checkout-page'});
            },
            toggleSection(section) {
                if (!this.page.hasMarkup(section)) {
                    this.page.addMarkup(new CheckoutPageMarkupModel({section}));
                } else {
                    this.page.removeMarkup({section});
                }
            },
            toggleBodySections() {
                const section = CheckoutPageMarkupModel.Sections.body;
                if (!this.page.hasMarkup(section)) {
                    this.page.addMarkup(new CheckoutPageMarkupModel({section}));
                } else {
                    this.page.removeAllMarkup(section);
                }
            },
            toggleSuggestedUpgrade(value) {
                this.page.suggestedUpgrade = value === null
                    ? null
                    : new SuggestedUpgradeModel();
            },
            updateSuggestedUpgrade(suggestedUpgrade) {
                this.page.suggestedUpgrade = suggestedUpgrade;
            },
            toggleOTO(value) {
                this.page.oneTimeOffer = value === null
                    ? null
                    : new OneTimeOfferModel();
            },
            toggleThankYouOTO() {
                this.page.thankYouOneTimeOffer = this.page.hasThankYouOTO
                    ? null
                    : new ThankYouOneTimeOfferModel();
            },
            updateOTO(oneTimeOffer) {
                this.page.oneTimeOffer = oneTimeOffer;
            },
            addBodySection() {
                const section = CheckoutPageMarkupModel.Sections.body;
                const markup = new CheckoutPageMarkupModel({section});
                this.page.addMarkup(markup);
            },
            updateBody(markup) {
                this.page.markup = markup;
            },
            updateFormSettings(page) {
                this.page = page;
            },
            updateLogo(file) {
                this.logo = file;
            },
            setNoLogo() {
                this.logo = null;
                const markup = this.page
                    .getMarkup({section: CheckoutPageMarkupModel.Sections.header});
                markup.content = '';
            },
            /**
             * Upload the logo file and update the content of the header section to display it.
             * @return {Promise<void>}
             */
            async uploadLogo() {
                const form = new FormData();
                form.append('file', this.logo);
                const file = await this.$api(
                    ({files}) => files.upload({fileObject: form}),
                );
                const params = file.getJSON().fields;
                params.isPublic = true;
                const {fields: imgFields} = await this.$api(
                    ({files}) => files.update({id: params.id, data: params}),
                );
                // eslint-disable-next-line
                const permalink = imgFields._links.find(link => link.rel === 'permalink');
                if (permalink) {
                    const content = `<img src="${permalink.href}" alt="Merchant logo image">`;
                    const markup = this.page
                        .getMarkup({section: CheckoutPageMarkupModel.Sections.header});
                    markup.content = content;
                }
            },
            async create(page) {
                try {
                    if (this.logo !== null) {
                        await this.uploadLogo();
                    }
                    const data = page.getPayload();
                    Reflect.deleteProperty(data, 'id');
                    const {fields} = await this.$api(({checkoutPages}) => checkoutPages.create({data}));
                    this.page = new CheckoutPageModel(fields);
                    this.isDataReady = true;
                    this.$router.push({
                        name: 'update-checkout-page',
                        params: {id: fields.id},
                        query: {newRecord: true},
                    });
                    this.$toast.positive('Checkout page successfully created');
                    this.triggerIntercomEvent({
                        name: 'Created a hosted checkout page',
                        meta: {
                            name: fields.name,
                        },
                    });
                } catch (error) {
                    this.$toast.fromError(error, 'Checkout page could not be saved');
                    throw error;
                }
            },
            async update(page) {
                try {
                    if (this.logo !== null) {
                        await this.uploadLogo();
                    }
                    const params = {
                        id: page.id,
                        data: page.getPayload(),
                    };
                    const {fields} = await this.$api(({checkoutPages}) => checkoutPages.update(params));
                    this.page = new CheckoutPageModel(fields);
                    this.$toast.positive('Checkout page successfully updated');
                } catch (error) {
                    this.$toast.fromError(error, 'Checkout page could not be updated');
                    throw error;
                }
            },
            async deletePage() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Delete ${this.$filters.unKebabCase(this.page.name)}`,
                    content: `Are you sure you want to delete this checkout page? This action cannot be undone.`,
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    try {
                        this.isLoading = true;
                        const params = {
                            id: this.page.id,
                        };
                        await this.$api(({checkoutPages}) => checkoutPages.delete(params));
                        this.$toast.positive('Checkout page successfully deleted');
                        this.$router.push({name: 'checkout-pages'});
                    } catch (error) {
                        this.$toast.fromError(error, 'Checkout page could not be deleted');
                        throw error;
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
            async fetchData() {
                try {
                    const colParams = {limit: 1000};
                    const [{items: websiteCol}, {items: plansCol}] = await this.$api(({websites, plans}) => Promise.all([websites.getAll(colParams), plans.getAll(colParams)]));
                    this.plans = plansCol.map(({fields}) => ({label: fields.name, value: fields.id}));
                    this.websites = websiteCol.map(({fields}) => ({label: fields.name, value: fields.id}));
                    // load checkout page if updating
                    if (this.isFormUpdating) {
                        const params = {id: this.id};
                        const {fields} = await this.$api(({checkoutPages}) => checkoutPages.get(params));
                        this.page = new CheckoutPageModel(fields);
                    }
                } catch (error) {
                    this.$toast.fromError(error, 'Checkout page data could not be retrieved');
                    this.$redirectOnError(error, {name: 'checkout-pages'});
                }
            },
        },
        created() {
            const clone = sessionStore.getItem(cloningStorageKey);
            if (clone) {
                // clone existing
                this.page = new CheckoutPageModel(clone);
                sessionStore.removeItem(cloningStorageKey);
            }
        },
    };
</script>
